/* Jevana AI Skin Analysis — vanilla JS, no dependencies.
 * Layers: (1) UI/flow  (2) API client  (3) recommender (uses the real Shopify catalogue)  (4) cart
 * No secrets live here. The AI key stays on the backend. */
(() => {
  'use strict';

  const rootEl = document.getElementById('jv-root');
  if (!rootEl) return;
  const $ = (id) => document.getElementById(id);

  const cfg = {
    photo: rootEl.dataset.photo === 'true',
    answersOnly: rootEl.dataset.photo !== 'true',
    engine: rootEl.dataset.engine || 'device',
    deviceScan: rootEl.dataset.photo === 'true' && (rootEl.dataset.engine || 'device') !== 'api',
    backend: rootEl.dataset.photo === 'true' && rootEl.dataset.engine === 'api' ? (rootEl.dataset.backend || '').trim() : '',
    currency: rootEl.dataset.currency || 'INR',
    shopRoot: rootEl.dataset.root || '/',
    loggedIn: rootEl.dataset.loggedIn === 'true',
    loginUrl: rootEl.dataset.loginUrl || '/account/login',
    loginTrigger: (rootEl.dataset.loginTrigger || '').trim(),
    provider: rootEl.dataset.provider || ''
  };
  const STORE_KEY = 'jv-session-v1';
  const MAX_ORIGINAL_MB = 25;
  const MAX_DIM = 1280;
  const REQUEST_TIMEOUT_MS = 75000;

  const state = {
    profile: null, blob: null, previewUrl: null,
    analysis: null, demo: rootEl.dataset.photo === 'true' && rootEl.dataset.engine === 'api' && !(rootEl.dataset.backend || '').trim(),
    skipped: false,
    catalog: null, recs: [], showAll: false, stream: null, loaderTimer: null
  };

  /* ------------------------------------------------------------------ helpers */
  function h(tag, attrs, ...kids) {
    const n = document.createElement(tag);
    for (const [k, v] of Object.entries(attrs || {})) {
      if (v == null || v === false) continue;
      if (k === 'class') n.className = v;
      else if (k === 'text') n.textContent = v;
      else n.setAttribute(k, v === true ? '' : v);
    }
    for (const kid of kids.flat()) {
      if (kid == null || kid === false) continue;
      n.append(kid.nodeType ? kid : document.createTextNode(String(kid)));
    }
    return n;
  }
  const clear = (n) => { while (n.firstChild) n.removeChild(n.firstChild); };
  const show = (n, on = true) => { if (n) n.hidden = !on; };
  const delay = (ms) => new Promise((r) => setTimeout(r, ms));
  const label = (s) => String(s || '').replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
  const money = (v) => {
    try { return new Intl.NumberFormat(undefined, { style: 'currency', currency: cfg.currency }).format(Number(v)); }
    catch { return `${cfg.currency} ${v}`; }
  };

  /* ------------------------------------------------------------------ panels */
  const PANELS = { 1: 'jv-form-profile', 2: 'jv-panel-photo', 3: 'jv-panel-loading', error: 'jv-panel-error' };
  function showPanel(key) {
    Object.entries(PANELS).forEach(([k, id]) => show($(id), k === String(key)));
    const idx = key === 'error' ? 2 : Number(key);
    document.querySelectorAll('[data-step-ind]').forEach((li) => {
      const n = Number(li.dataset.stepInd);
      li.classList.toggle('is-current', n === idx);
      li.classList.toggle('is-done', n < idx);
      if (n === idx) li.setAttribute('aria-current', 'step'); else li.removeAttribute('aria-current');
    });
    const heading = $(PANELS[key]).querySelector('h3');
    if (heading) { heading.tabIndex = -1; heading.focus({ preventScroll: true }); }
    $('jv-flow').scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  /* ------------------------------------------------------------------ step 1 */
  function readProfile() {
    const f = $('jv-form-profile');
    const fd = new FormData(f);
    return {
      ageRange: fd.get('ageRange') || '',
      skinType: fd.get('skinType') || '',
      concerns: fd.getAll('concerns'),
      sensitivity: fd.get('sensitivity') || '',
      currentRoutine: (fd.get('currentRoutine') || '').toString().trim(),
      allergies: (fd.get('allergies') || '').toString().trim(),
      diet: fd.get('diet') || '',
      foodAllergies: (fd.get('foodAllergies') || '').toString().trim()
    };
  }

  $('jv-form-profile').addEventListener('submit', (e) => {
    e.preventDefault();
    const p = readProfile();
    const missing = [];
    if (!p.ageRange) missing.push('age range');
    if (!p.skinType) missing.push('skin type');
    if (!p.sensitivity) missing.push('sensitivity');
    if (!p.diet) missing.push('food preference');
    const err = $('jv-profile-error');
    if (missing.length) {
      err.textContent = `Please choose: ${missing.join(', ')}.`;
      show(err);
      return;
    }
    show(err, false);
    state.profile = p;
    if (cfg.answersOnly) { if (isLoggedIn()) runAnalysis(); else goToLogin(); return; }
    showPanel(2);
  });

  /* ------------------------------------------------------------------ step 2: photo */
  function setPhotoError(msg) { const e = $('jv-photo-error'); e.textContent = msg || ''; show(e, !!msg); }

  async function toBitmap(file) {
    if ('createImageBitmap' in window) {
      try { return await createImageBitmap(file, { imageOrientation: 'from-image' }); } catch { /* fall through */ }
    }
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
      img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('decode')); };
      img.src = url;
    });
  }

  /** Re-encodes to a ≤1280px JPEG. This also strips EXIF/location metadata. */
  async function normaliseImage(source) {
    const bmp = source instanceof Blob ? await toBitmap(source) : source;
    const w = bmp.width || bmp.videoWidth, hgt = bmp.height || bmp.videoHeight;
    const scale = Math.min(1, MAX_DIM / Math.max(w, hgt));
    const c = document.createElement('canvas');
    c.width = Math.round(w * scale); c.height = Math.round(hgt * scale);
    c.getContext('2d').drawImage(bmp, 0, 0, c.width, c.height);
    if (bmp.close) bmp.close();
    return new Promise((res, rej) => c.toBlob((b) => (b ? res(b) : rej(new Error('encode'))), 'image/jpeg', 0.86));
  }

  async function acceptPhoto(file) {
    setPhotoError('');
    if (!file) return;
    if (!file.type.startsWith('image/')) return setPhotoError('Please choose an image file (JPEG, PNG or WebP).');
    if (file.size > MAX_ORIGINAL_MB * 1024 * 1024) return setPhotoError(`That photo is too large (max ${MAX_ORIGINAL_MB} MB).`);
    try {
      const blob = await normaliseImage(file);
      setBlob(blob);
    } catch {
      setPhotoError('We could not read that photo. Please try a JPEG or PNG.');
    }
  }

  function setBlob(blob) {
    if (state.previewUrl) URL.revokeObjectURL(state.previewUrl);
    state.blob = blob;
    state.previewUrl = URL.createObjectURL(blob);
    $('jv-preview').src = state.previewUrl;
    show($('jv-preview')); show($('jv-photo-empty'), false); show($('jv-photo-actions'));
    updateAnalyzeBtn();
  }

  function clearPhoto() {
    if (state.previewUrl) URL.revokeObjectURL(state.previewUrl);
    state.blob = null; state.previewUrl = null;
    clearPhotoMap();
    $('jv-preview').removeAttribute('src');
    $('jv-file').value = ''; $('jv-file-capture').value = '';
    show($('jv-preview'), false); show($('jv-photo-empty')); show($('jv-photo-actions'), false);
    setPhotoError('');
    updateAnalyzeBtn();
  }

  function updateAnalyzeBtn() {
    // In demo mode no photo is processed, so a photo is optional there.
    const needPhoto = cfg.deviceScan || !!cfg.backend;
    $('jv-analyze').disabled = !($('jv-consent').checked && (!needPhoto || state.blob));
  }

  $('jv-file').addEventListener('change', (e) => acceptPhoto(e.target.files[0]));
  $('jv-file-capture').addEventListener('change', (e) => acceptPhoto(e.target.files[0]));
  $('jv-consent').addEventListener('change', updateAnalyzeBtn);
  $('jv-remove').addEventListener('click', clearPhoto);
  $('jv-retake').addEventListener('click', () => { clearPhoto(); openCamera(); });
  $('jv-back-1').addEventListener('click', () => showPanel(1));
  $('jv-error-back').addEventListener('click', () => { clearPhoto(); showPanel(cfg.answersOnly ? 1 : 2); });
  $('jv-retry').addEventListener('click', () => showPanel(cfg.answersOnly ? 1 : 2));

  /* camera */
  const dlg = $('jv-camera-dialog');
  async function openCamera() {
    if (!navigator.mediaDevices?.getUserMedia || typeof dlg.showModal !== 'function') {
      $('jv-file-capture').click();   // native camera on phones, file picker elsewhere
      return;
    }
    const err = $('jv-camera-error'); show(err, false);
    dlg.showModal();
    try {
      state.stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user', width: { ideal: 1280 } }, audio: false });
      const v = $('jv-video'); v.srcObject = state.stream; await v.play();
    } catch {
      err.textContent = 'Camera access was blocked or is unavailable. You can upload a photo instead.';
      show(err);
    }
  }
  function closeCamera() {
    if (state.stream) state.stream.getTracks().forEach((t) => t.stop());
    state.stream = null; $('jv-video').srcObject = null;
    if (dlg.open) dlg.close();
  }
  $('jv-camera-btn').addEventListener('click', openCamera);
  $('jv-camera-cancel').addEventListener('click', closeCamera);
  dlg.addEventListener('close', closeCamera);
  $('jv-snap').addEventListener('click', async () => {
    const v = $('jv-video');
    if (!v.videoWidth) return;
    try { setBlob(await normaliseImage(v)); setPhotoError(''); } catch { setPhotoError('Could not capture the photo.'); }
    closeCamera();
  });

  /* ------------------------------------------------------------------ step 3: analysis */
  const REAL_MSGS = ['Reviewing visible skin characteristics…', 'Understanding your skincare preferences…', 'Preparing your personalized routine…'];
  const DEVICE_MSGS = ['Loading the on-device scanner (first time only)…', 'Checking your photo and looking at brightness and colour across your face…', 'Preparing your routine from your answers…'];
  function startLoader(demo) {
    const el = $('jv-loading-msg');
    stopLoader();
    if (demo && !cfg.deviceScan) { el.textContent = 'Preparing general guidance from your answers…'; return; }
    const msgs = cfg.deviceScan ? DEVICE_MSGS : REAL_MSGS;
    let i = 0; el.textContent = msgs[0];
    state.loaderTimer = setInterval(() => { i = Math.min(i + 1, msgs.length - 1); el.textContent = msgs[i]; }, cfg.deviceScan ? 3500 : 5000);
  }
  function stopLoader() { clearInterval(state.loaderTimer); state.loaderTimer = null; }

  async function requestAnalysis() {
    const fd = new FormData();
    fd.append('image', state.blob, 'selfie.jpg');
    fd.append('profile', JSON.stringify(state.profile));
    fd.append('consent', 'true');
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), REQUEST_TIMEOUT_MS);
    let res;
    try {
      res = await fetch(cfg.backend, { method: 'POST', body: fd, signal: ctrl.signal, credentials: 'omit', mode: 'cors' });
    } catch (e) {
      throw new Error(e.name === 'AbortError' ? 'The analysis took too long. Please try again.' : 'We could not reach the analysis service. Check your connection and try again.');
    } finally { clearTimeout(timer); }
    let json = null;
    try { json = await res.json(); } catch { /* ignore */ }
    if (!res.ok || !json?.ok) throw new Error(json?.message || 'The analysis service returned an error. Please try again.');
    return json.analysis;
  }

  $('jv-analyze').addEventListener('click', async () => {
    if (!state.profile) return showPanel(1);
    if (!isLoggedIn()) return goToLogin();
    return runAnalysis();
  });

  async function runAnalysis() {
    state.skipped = false;
    showPanel(3);
    startLoader(!cfg.backend);
    try {
      let analysis;
      if (cfg.deviceScan) analysis = await deviceAnalysis();
      else if (!cfg.backend) { await delay(600); analysis = demoAnalysis(state.profile); }
      else {
        analysis = await requestAnalysis();
        if (!analysis.photoUsable) {
          const e = new Error(`${analysis.photoNote} Please retake your photo and try again.`);
          throw e;
        }
      }
      stopLoader();
      state.analysis = analysis;
      persist();
      await renderAll(true);
    } catch (e) {
      stopLoader();
      $('jv-error-msg').textContent = e.message || 'Something went wrong. Please try again.';
      show($('jv-skip'), cfg.deviceScan);
      showPanel('error');
    }
  }

  /* ------------------------------------------------------------------ login gate: save → log in → resume → auto-scan */
  const PENDING_KEY = 'jv-pending-v1';
  /** KwikPass login detection: its tokens (ELRTOKEN / KWIKSESSIONTOKEN) are empty when logged out and set after OTP login.
   *  Shopify's own `customer` is not always updated by KwikPass without a reload, so we check both. */
  function kwikLoggedIn() {
    try {
      if (typeof window.getValueFromCookiesOrLocalStorage !== 'function') return false;
      const keys = [];
      try { keys.push(window.XGokwikUniToken()); } catch { /* not ready */ }
      try { keys.push(window.XGokwikCoreToken(window.merchantInfo && window.merchantInfo.environment)); } catch { /* not ready */ }
      return keys.filter(Boolean).some((k) => { try { return !!window.getValueFromCookiesOrLocalStorage(k); } catch { return false; } });
    } catch { return false; }
  }
  function isLoggedIn() { return cfg.loggedIn || kwikLoggedIn(); }
  function waitFor(fn, ms) {
    return new Promise((res) => {
      const t0 = Date.now();
      const tick = () => {
        let ok = false;
        try { ok = fn(); } catch { /* ignore */ }
        if (ok) return res(true);
        if (Date.now() - t0 > ms) return res(false);
        setTimeout(tick, 300);
      };
      tick();
    });
  }
  function refreshLoginLabels() {
    const li = isLoggedIn();
    if (cfg.answersOnly) {
      const b = $('jv-form-profile').querySelector('button[type=submit]');
      if (b) b.textContent = li ? 'Get my routine' : 'Log in & get my routine';
    } else if (cfg.deviceScan) {
      $('jv-analyze').textContent = li ? 'Scan my photo' : 'Log in & scan my photo';
    }
  }
  function blobToDataUrl(blob) {
    return new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(blob); });
  }
  /** Finds the site's own "log in" control automatically (KwikPass markers first, then account links, then any visible "Log in" text). */
  function loginCandidates() {
    const out = [];
    const visible = (el) => el.offsetParent !== null || getComputedStyle(el).position === 'fixed';
    const add = (el, why) => { if (el && !rootEl.contains(el) && !el.closest('footer') && visible(el) && !out.some((o) => o.el === el)) out.push({ el, why }); };
    document.querySelectorAll('[id*="kwik" i],[class*="kwik" i],[id^="kp-" i],[class*="kp-login" i],[data-kwikpass]').forEach((el) => {
      if (/^(a|button)$/i.test(el.tagName) || el.getAttribute('role') === 'button') add(el, 'kwikpass-marker');
    });
    ['a[href*="/customer_authentication/redirect"]', 'a[href*="/account/login"]'].forEach((sel) => {
      document.querySelectorAll(sel).forEach((el) => add(el, 'account-link'));
    });
    document.querySelectorAll('a,button,[role="button"]').forEach((el) => {
      const t = `${(el.textContent || '').trim()} ${el.getAttribute('aria-label') || ''}`.trim();
      if (t.length < 60 && /\b(log ?in|sign ?in)\b/i.test(t)) add(el, 'text-login');
    });
    return out;
  }
  /** Add ?jvdebug=1 to the page URL to print what the scanner found (for support). */
  function showLoginDebug(cands) {
    const lines = cands.map((c) => `${c.why} | <${c.el.tagName.toLowerCase()}> id="${c.el.id}" class="${c.el.className}" href="${c.el.getAttribute('href') || ''}" text="${(c.el.textContent || '').trim().slice(0, 40)}"`);
    const scripts = [...document.querySelectorAll('script[src]')].map((x) => x.src).filter((u) => /kwik/i.test(u));
    const globals = Object.keys(window).filter((k) => /kwik|^kp/i.test(k));
    $('jv-flow').append(h('pre', { style: 'white-space:pre-wrap;background:#fff;border:1px solid #ccc;padding:12px;font-size:12px;user-select:all' },
      `JEVANA LOGIN DEBUG\nLogin candidates (${cands.length}):\n${lines.join('\n') || '(none)'}\nKwikPass scripts:\n${scripts.join('\n') || '(none)'}\nKwikPass-like globals: ${globals.join(', ') || '(none)'}`));
  }
  async function goToLogin() {
    const btn = cfg.answersOnly ? $('jv-form-profile').querySelector('button[type=submit]') : $('jv-analyze');
    const original = btn.textContent;
    btn.disabled = true; btn.textContent = 'Taking you to log in…';
    let photo = null;
    try { if (state.blob) photo = await blobToDataUrl(state.blob); } catch { /* photo will be re-requested */ }
    try { sessionStorage.setItem(PENDING_KEY, JSON.stringify({ profile: state.profile, photo, consent: true, t: Date.now() })); }
    catch { try { sessionStorage.setItem(PENDING_KEY, JSON.stringify({ profile: state.profile, photo: null, consent: true, t: Date.now() })); } catch { /* storage blocked */ } }
    // Order: 1) selector from settings  2) custom login URL from settings
    //        3) the site's own header login link (so apps like KwikPass can intercept the click and show their popup)
    //        4) plain redirect to /account/login
    const returnUrl = cfg.loginUrl.replace('{return}', encodeURIComponent(window.location.pathname));
    let trigger = null;
    if (cfg.loginTrigger) { try { trigger = document.querySelector(cfg.loginTrigger); } catch { /* invalid selector */ } }
    let link = null;
    const cands = (!trigger && cfg.loginUrl.includes('/account/login')) ? loginCandidates() : [];
    if (/[?&]jvdebug=1/.test(window.location.search)) { showLoginDebug(cands); btn.disabled = false; btn.textContent = original; return; }
    // KwikPass: kpHandleLogin(redirectUrl) opens its login popup (or redirects if already logged in).
    if (!trigger && typeof window.kpHandleLogin === 'function') {
      try {
        btn.textContent = 'Opening log in…';
        window.kpHandleLogin(window.location.pathname + window.location.search);
        watchForLogin();
        setTimeout(() => { btn.disabled = false; btn.textContent = original; }, 2500);
        return;
      } catch { /* KwikPass not ready: fall through to link click / redirect */ }
    }
    if (cands.length) link = cands[0].el;
    if (trigger || link) {
      btn.textContent = 'Opening log in…';
      if (trigger) trigger.click();
      else {
        const oldHref = link.getAttribute('href') || '';
        const swap = oldHref.includes('/account/login');   // only add return_url to classic login links
        if (swap) link.setAttribute('href', returnUrl);
        link.click();
        if (swap) setTimeout(() => link.setAttribute('href', oldHref), 0);
      }
      watchForLogin();
      setTimeout(() => { btn.disabled = false; btn.textContent = original; }, 2500);
      return;
    }
    window.location.href = returnUrl;
  }
  /** If a popup login (e.g. KwikPass) succeeds without reloading the page, notice it and reload so the scan resumes. */
  let loginWatch = null;
  function watchForLogin() {
    clearInterval(loginWatch);
    const sec = rootEl.closest('[id^="shopify-section-"]');
    const id = sec ? sec.id.replace('shopify-section-', '') : '';
    let tries = 0;
    loginWatch = setInterval(async () => {
      if (++tries > 180) { clearInterval(loginWatch); return; }            // give up after ~3 minutes
      if (kwikLoggedIn()) { clearInterval(loginWatch); onLoggedInInPlace(); return; }
      if (id && tries % 3 === 0) {                                          // Shopify session appeared (e.g. classic login)
        try {
          const r = await fetch(`${window.location.pathname}?sections=${encodeURIComponent(id)}`, { cache: 'no-store', credentials: 'same-origin' });
          const j = await r.json();
          if ((j[id] || '').includes('data-logged-in="true"')) { clearInterval(loginWatch); window.location.reload(); }
        } catch { /* keep waiting */ }
      }
    }, 1000);
  }
  /** Popup login finished without a page reload: our answers/photo are still in memory, so just carry on. */
  function onLoggedInInPlace() {
    try { sessionStorage.removeItem(PENDING_KEY); } catch { /* ignore */ }
    refreshLoginLabels();
    if (!state.profile) return;
    setTimeout(() => runAnalysis(), 800);
  }
  async function resumePending() {
    let pending = null;
    try { pending = JSON.parse(sessionStorage.getItem(PENDING_KEY) || 'null'); } catch { /* ignore */ }
    try { sessionStorage.removeItem(PENDING_KEY); } catch { /* ignore */ }   // delete immediately after reading
    if (!pending?.profile || !isLoggedIn()) return false;
    if (pending.t && Date.now() - pending.t > 30 * 60 * 1000) return false;   // stale (30 min)
    state.profile = pending.profile;
    if (pending.photo) {
      try { setBlob(await (await fetch(pending.photo)).blob()); } catch { /* fall through */ }
    }
    if ((cfg.backend || cfg.deviceScan) && !state.blob) {            // photo didn't survive the redirect: ask again
      $('jv-consent').checked = !!pending.consent; updateAnalyzeBtn();
      showPanel(2);
      setPhotoError('Welcome back! Please add your photo again to continue.');
      return true;
    }
    $('jv-consent').checked = !!pending.consent;
    updateAnalyzeBtn();
    await runAnalysis();
    return true;
  }

  /* ------------------------------------------------------------------ persistence (session only, never the photo) */
  function persist() {
    try { sessionStorage.setItem(STORE_KEY, JSON.stringify({ profile: state.profile, analysis: state.analysis, demo: state.demo, skipped: state.skipped })); } catch { /* storage unavailable */ }
  }
  function loadSaved() {
    try { return JSON.parse(sessionStorage.getItem(STORE_KEY) || 'null'); } catch { return null; }
  }
  function wipe() { try { sessionStorage.removeItem(STORE_KEY); } catch { /* noop */ } }

  /* ------------------------------------------------------------------ rendering */
  function renderItems(container, items, mapper, emptyText) {
    clear(container);
    if (!items?.length) { container.append(h('p', { class: 'jv-na', text: emptyText })); return; }
    items.forEach((it) => container.append(mapper(it)));
  }
  function fillList(ul, items, emptyText) {
    clear(ul);
    if (!items?.length) { ul.append(h('li', { class: 'jv-na', text: emptyText })); return; }
    items.forEach((t) => ul.append(h('li', { text: t })));
  }

  const SVG_NS = 'http://www.w3.org/2000/svg';
  const ZONES = {
    forehead: [{ cx: 100, cy: 62, rx: 46, ry: 20 }],
    nose: [{ cx: 100, cy: 135, rx: 13, ry: 30 }],
    left_cheek: [{ cx: 62, cy: 152, rx: 22, ry: 26 }],
    right_cheek: [{ cx: 138, cy: 152, rx: 22, ry: 26 }],
    chin: [{ cx: 100, cy: 206, rx: 26, ry: 14 }],
    under_eyes: [{ cx: 74, cy: 110, rx: 17, ry: 7 }, { cx: 126, cy: 110, rx: 17, ry: 7 }]
  };
  function buildMap(areas) {
    const svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('viewBox', '0 0 200 260');
    svg.setAttribute('role', 'img');
    const active = areas.map((a) => a.region);
    svg.setAttribute('aria-label', active.length ? `Illustration highlighting: ${active.map(label).join(', ')}` : 'Face illustration with no highlighted areas');
    const face = document.createElementNS(SVG_NS, 'ellipse');
    [['cx', 100], ['cy', 130], ['rx', 72], ['ry', 100], ['stroke-width', 2]].forEach(([k, v]) => face.setAttribute(k, v));
    face.style.fill = 'var(--jv-lav-2)'; face.style.stroke = 'var(--jv-purple)';
    svg.append(face);
    Object.entries(ZONES).forEach(([name, shapes]) => {
      const on = active.includes(name);
      shapes.forEach((s) => {
        const e = document.createElementNS(SVG_NS, 'ellipse');
        Object.entries(s).forEach(([k, v]) => e.setAttribute(k, v));
        e.style.fill = on ? 'var(--jv-purple-2)' : 'none';
        e.style.fillOpacity = on ? '0.45' : '1';
        e.style.stroke = on ? 'var(--jv-purple-2)' : 'var(--jv-beige)';
        e.setAttribute('stroke-dasharray', on ? '' : '3 3');
        svg.append(e);
      });
    });
    return svg;
  }

  /** Short, general, non-medical "what to do" lines. Matched on the issue's wording; never invented for unknown issues. */
  const FIXES = [
    [/acne|blemish|breakout|pimple/i, 'Wash gently, don’t pick. Neem, tulsi or sandalwood care can help.'],
    [/pore/i, 'Use a mild clay or sandalwood face pack once a week.'],
    [/under.?eye|dark circle/i, 'Sleep well, drink water, and try a cool rose-water compress.'],
    [/tone|pigment|dark spot|patch|discolo/i, 'Turmeric or saffron based care, used regularly, can help.'],
    [/dry|dehydrat|flak/i, 'Moisturise on damp skin. Aloe, almond or coconut care helps.'],
    [/oil|shine|greas|sebum/i, 'Wash gently, use a light gel, and a weekly clay pack.'],
    [/dull|glow|radian/i, 'Drink water, sleep well, and use a weekly turmeric or honey pack.'],
    [/fine line|wrinkle|firm|ageing|aging/i, 'Keep skin well moisturised with nourishing oils, and sleep well.'],
    [/red|irritat|sensitiv|flush/i, 'Keep it simple, avoid harsh scrubs, and patch test first.']
  ];
  function fixFor(text) { const f = FIXES.find(([re]) => re.test(text)); return f ? f[1] : ''; }

  function renderFix() {
    const a = state.analysis, list = $('jv-fix'); clear(list);
    const first = (a.skinTypeGuidance || '').split(/(?<=[.!?])\s/)[0];
    $('jv-fix-type').textContent = clip(first || '', 90);
    const showNote = cfg.photo && !state.skipped && !state.demo;     // notes are only meaningful when a photo was really scanned
    const rows = [];
    const seen = new Set();
    const add = (issue, note) => {
      const sol = fixFor(`${issue} ${note || ''}`);
      const key = issue.toLowerCase();
      if (!sol || seen.has(key)) return;
      seen.add(key); rows.push({ issue, note: showNote && note !== CONCERN_NOTE ? note : '', sol });
    };
    (a.possibleConcerns || []).forEach((c) => add(c.concern, c.note));
    if (showNote) (a.observations || []).filter((o) => !/^(about this scan|photo quality|overall)$/i.test(o.area)).forEach((o) => add(o.area, o.note));
    if (!rows.length) {
      list.append(h('li', { class: 'jv-fixrow' }, h('div', { class: 'jv-fixrow__issue' }, h('span', { class: 'jv-fixrow__k', text: 'Issue' }), 'Nothing specific flagged'),
        h('div', { class: 'jv-fixrow__sol' }, h('span', { class: 'jv-fixrow__k', text: 'Solution' }), 'Keep it simple: a gentle cleanser and a moisturiser.')));
      return;
    }
    rows.forEach((r) => list.append(h('li', { class: 'jv-fixrow' },
      h('div', { class: 'jv-fixrow__issue' }, h('span', { class: 'jv-fixrow__k', text: 'Issue' }), r.issue, r.note && h('small', { text: r.note })),
      h('div', { class: 'jv-fixrow__sol' }, h('span', { class: 'jv-fixrow__k', text: 'Solution' }), r.sol))));
  }

  /** Shows the shopper's own photo with numbered outlines where the scan noticed something. The photo stays in this tab only. */
  function clearPhotoMap() {
    const card = $('jv-photocard'); if (!card) return;
    clear($('jv-photomap')); clear($('jv-photolegend')); card.hidden = true;
  }
  function renderPhotoMap() {
    const card = $('jv-photocard'); if (!card) return;
    const marks = state.analysis?.photoMarks;
    if (!cfg.deviceScan || state.skipped || !state.previewUrl || !marks) { clearPhotoMap(); return; }
    const box = $('jv-photomap'), legend = $('jv-photolegend');
    clear(box); clear(legend);
    const img = h('img', { src: state.previewUrl, alt: 'Your photo with the areas we noticed outlined', class: 'jv-photomap__img' });
    const svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('viewBox', '0 0 1 1'); svg.setAttribute('preserveAspectRatio', 'none'); svg.setAttribute('class', 'jv-photomap__svg'); svg.setAttribute('aria-hidden', 'true');
    const pins = [];
    marks.forEach((m, i) => {
      m.shapes.forEach((e) => {
        ['jv-ring-dark', 'jv-ring'].forEach((cls) => {
          const el = document.createElementNS(SVG_NS, 'ellipse');
          ['cx', 'cy', 'rx', 'ry'].forEach((k) => el.setAttribute(k, e[k]));
          el.setAttribute('class', cls); svg.append(el);
        });
      });
      const top = m.shapes.reduce((a, b) => (b.cy - b.ry < a.cy - a.ry ? b : a));
      pins.push(h('span', { class: 'jv-pin', style: `left:${(top.cx * 100).toFixed(1)}%;top:${Math.max(2, (top.cy - top.ry) * 100).toFixed(1)}%`, text: String(i + 1) }));
      legend.append(h('li', {}, h('span', { class: 'jv-pin jv-pin--static', text: String(i + 1) }), h('strong', { text: m.label }), ` · ${m.where}`));
    });
    box.append(img, svg, ...pins);
    if (!marks.length) legend.append(h('li', { class: 'jv-na', text: 'Nothing stood out in this photo.' }));
    card.hidden = false;
  }

  function renderDashboard() {
    renderPhotoMap();
    const a = state.analysis;
    show($('jv-results-demo'), !!state.demo);
    show($('jv-results-device'), cfg.deviceScan && !state.skipped);

    const derm = $('jv-derm');
    if (a.seeDermatologist?.suggested) {
      clear(derm);
      derm.append(h('strong', { text: 'Consider seeing a dermatologist. ' }), a.seeDermatologist.reason);
      show(derm);
    } else show(derm, false);

    renderItems($('jv-observations'), a.observations, (o) => h('div', { class: 'jv-item' }, h('strong', { text: o.area }), o.note),
      state.skipped ? 'No photo was scanned, so this guidance uses your answers only.' : state.demo ? 'Not available in demo mode — no photo analysis was performed.' : 'No specific visible characteristics were noted.');
    renderFix();
    $('jv-guidance').textContent = a.skinTypeGuidance;
    const mapCard = $('jv-mapcard'); if (mapCard) mapCard.hidden = !cfg.photo || (cfg.deviceScan && !state.skipped);
    const mapBox = $('jv-map'); clear(mapBox); mapBox.append(buildMap(a.attentionAreas || []));
    renderItems($('jv-areas'), a.attentionAreas, (x) => h('div', { class: 'jv-item' }, h('strong', { text: label(x.region) }), x.note),
      state.skipped ? 'No photo was scanned.' : state.demo ? 'Highlighted areas need a photo analysis.' : 'No areas stood out.');
    fillList($('jv-explore'), a.ingredientsToExplore, 'No specific ingredient suggestions.');
    fillList($('jv-avoid'), a.ingredientsToAvoid, 'Nothing specific to flag.');
    fillList($('jv-habits'), a.dailyHabits, 'Cleanse gently and moisturise daily.');
  }

  const GLOW = [
    ['hydration', 'Hydration'], ['meals', 'Balanced meals'], ['protein', 'Protein-rich options'],
    ['fruitsVegetables', 'Fruits & vegetables'], ['healthyFats', 'Healthy fats'], ['sleepStress', 'Sleep & stress']
  ];
  function renderGlow() {
    const L = state.analysis.lifestyle || {};
    const grid = $('jv-glow-grid'); clear(grid);
    GLOW.forEach(([key, title]) => {
      if (!L[key]?.length) return;
      grid.append(h('article', { class: 'jv-card jv-glowcard' }, h('h3', { class: 'jv-h3', text: title }),
        h('ul', { class: 'jv-list' }, L[key].map((t) => h('li', { text: t })))));
    });
    if (L.checklist?.length) {
      const ul = h('ul', { class: 'jv-checklist' });
      L.checklist.forEach((t, i) => {
        const id = `jv-chk-${i}`;
        ul.append(h('li', {}, h('label', { for: id }, h('input', { type: 'checkbox', id }), h('span', { text: t }))));
      });
      grid.append(h('article', { class: 'jv-card jv-glowcard' }, h('h3', { class: 'jv-h3', text: 'Daily wellness checklist' }), ul));
    }
  }

  function renderRoutine() {
    const a = state.analysis;
    const build = (ol, steps) => {
      clear(ol);
      if (!steps?.length) { ol.append(h('li', { class: 'jv-na', text: 'No steps suggested.' })); return; }
      steps.forEach((s) => {
        const rec = state.recs.find((r) => r.need.category === s.category);
        ol.append(h('li', {},
          h('div', { class: 'jv-cat', text: label(s.category) }),
          h('strong', { text: s.title }),
          h('div', { text: s.howTo }),
          s.frequency && h('div', { class: 'jv-muted', text: `How often: ${s.frequency}` }),
          s.precautions && h('div', { class: 'jv-prec', text: `Note: ${s.precautions}` }),
          rec && h('div', {}, h('a', { href: '#jv-products', text: `See ${rec.product.title} below` }))
        ));
      });
    };
    build($('jv-am'), a.morningRoutine);
    build($('jv-pm'), a.eveningRoutine);
  }

  async function renderAll(scroll) {
    renderDashboard();
    renderGlow();
    show($('jv-results-wrap'));
    $('jv-products-status').textContent = 'Looking through our range…';
    clear($('jv-products-grid')); show($('jv-more'), false); show($('jv-products-empty'), false);
    renderRoutine();
    if (scroll) $('jv-results').scrollIntoView({ behavior: 'smooth', block: 'start' });
    await renderProducts();
    renderRoutine(); // re-render to link matched products
  }

  /* ------------------------------------------------------------------ recommender (real Shopify catalogue only) */
  const CATEGORY_TERMS = {
    cleanser: ['cleanser', 'face wash', 'facewash', 'cleansing', 'soap', 'cleansing bar'],
    toner: ['toner', 'face mist', 'essence'],
    serum: ['serum', 'ampoule'],
    moisturizer: ['moisturizer', 'moisturiser', 'face cream', 'cream', 'lotion', 'hydrating gel', 'gel', 'butter', 'balm', 'ghee'],
    sunscreen: ['sunscreen', 'spf', 'sun screen', 'sunblock'],
    face_oil: ['face oil', 'facial oil', 'tailam', 'thailam', 'kumkumadi', 'serum oil'],
    mask: ['face mask', 'mask', 'face pack', 'facepack', 'ubtan', 'mud', 'multani', 'pack'],
    exfoliant: ['exfoliant', 'scrub', 'peel'],
    eye_care: ['eye cream', 'eye gel', 'under eye', 'under-eye'],
    spot_care: ['spot treatment', 'blemish', 'acne patch']
  };
  const CONCERN_TERMS = {
    acne: ['acne', 'blemish', 'breakout', 'pimple', 'neem', 'tulsi', 'sandal', 'tea tree', 'clay', 'multani'],
    pores: ['pore', 'multani', 'clay', 'sandal', 'rose', 'neem'],
    uneven_tone: ['even tone', 'tone', 'brighten', 'dark spot', 'pigment', 'tan', 'turmeric', 'saffron', 'kesar', 'kumkumadi', 'manjistha', 'licorice'],
    dryness: ['dry', 'hydrat', 'moistur', 'nourish', 'aloe', 'honey', 'almond', 'coconut', 'shea', 'ghee', 'sesame'],
    excess_oil: ['oil control', 'oily', 'balanc', 'clay', 'multani', 'neem', 'sandal', 'mattif'],
    dullness: ['dull', 'glow', 'radiance', 'brighten', 'saffron', 'kesar', 'kumkumadi', 'turmeric', 'rose', 'honey'],
    fine_lines: ['fine line', 'wrinkle', 'firming', 'anti-ageing', 'anti-aging', 'anti ageing', 'nourish', 'saffron', 'almond', 'ashwagandha']
  };

  /** Benefits come from the product's OWN page text (its description), never from a generic list.
   *  1) bullets under a "Benefits / Key benefits / Why you'll love it" heading, 2) any other bullet points, 3) benefit-style sentences. */
  const BENEFIT_HEAD = /^(key |main |top )?(benefits?|results?|why you.?ll love( it)?|why choose|highlights?|key features?|features?)\b/i;
  const NOT_BENEFIT = /^(ingredients?|how to|directions?|usage|apply|size|net (wt|weight|qty)|quantity|mrp|shelf|country|manufactur|marketed|warning|caution|disclaimer|storage|pack|volume|for external)/i;
  const BENEFIT_VERBS = /\b(helps?|reduces?|hydrates?|moisturi[sz]es?|brightens?|soothes?|nourishes?|protects?|controls?|improves?|lightens?|fades?|smooth(s|es)?|clears?|unclogs?|calms?|repairs?|firms?|tones?|evens?|replenish(es)?|refreshes?|cleanses?|gives?|leaves?|restores?|boosts?|prevents?|minimi[sz]es?|balances?|softens?|absorbs?|tightens?|removes?|fights?|glow|radiance)\b/i;
  function cleanLine(t) {
    return String(t || '').replace(/\s+/g, ' ').replace(/^[\s•\-–—*✓✔☑►▪·]+/, '').replace(/[\s;,]+$/, '').trim();
  }
  function clip(t, n = 110) { return t.length <= n ? t : `${t.slice(0, n).replace(/\s+\S*$/, '')}…`; }
  function extractBenefits(html) {
    const doc = new DOMParser().parseFromString(html || '', 'text/html');
    const good = (t) => t.length >= 8 && t.length <= 160 && !NOT_BENEFIT.test(t);
    const liTexts = (root) => [...root.querySelectorAll('li')].map((li) => cleanLine(li.textContent)).filter(good);
    let out = [];
    // 1) bullets under a "Benefits" style heading
    const heads = [...doc.body.querySelectorAll('h1,h2,h3,h4,h5,h6,p,strong,b,span,div,li')].filter((e) => {
      const t = cleanLine(e.textContent).replace(/[:：]$/, '');
      return t.length <= 40 && BENEFIT_HEAD.test(t) && !e.querySelector('ul,ol');
    });
    for (const hd of heads) {
      let node = hd, tries = 0;
      while (node && node !== doc.body && !node.nextElementSibling && tries++ < 3) node = node.parentElement;   // heading wrapped in <p><strong>…
      let sib = node && node.nextElementSibling, hops = 0;
      while (sib && hops++ < 3) {
        if (/^(UL|OL)$/.test(sib.tagName) || sib.querySelector?.('ul,ol')) { out = liTexts(sib); break; }
        if (/^H[1-6]$/.test(sib.tagName)) break;
        if (sib.tagName === 'P' && good(cleanLine(sib.textContent)) && !out.length) { out = [cleanLine(sib.textContent)]; }
        sib = sib.nextElementSibling;
      }
      if (out.length) break;
    }
    // 2) any bullet points on the page
    if (!out.length) out = liTexts(doc.body);
    // 3) benefit-style sentences from the description
    if (!out.length) {
      const text = (doc.body.textContent || '').replace(/\s+/g, ' ');
      out = text.split(/(?<=[.!?])\s+/).map(cleanLine).filter((t) => good(t) && BENEFIT_VERBS.test(t));
    }
    return [...new Set(out.map((t) => clip(t.replace(/[.]+$/, ''))))];
  }
  /** Pick up to 3, putting the ones that mention the shopper's concerns / the matched keywords first. */
  function pickBenefits(p, terms) {
    const ts = (terms || []).map((x) => x.toLowerCase());
    const score = (t) => ts.filter((k) => has(t.toLowerCase(), k)).length;
    return (p.benefitLines || []).map((t, i) => ({ t, i, sc: score(t) })).sort((x, y) => y.sc - x.sc || x.i - y.i).slice(0, 3).map((x) => x.t);
  }
  function concernsFor(p, profile) {
    return (profile.concerns || []).filter((c) => (CONCERN_TERMS[c] || []).some((t) => has(p.full, t))).map(label);
  }
  const STRONG_ACTIVES = ['retinol', 'retinoid', 'glycolic', 'salicylic', 'lactic acid', 'aha', 'bha', 'kojic', 'benzoyl'];
  const SKIN_ALLERGEN_MAP = {
    fragrance: ['fragrance', 'parfum', 'perfume'],
    perfume: ['fragrance', 'parfum', 'perfume'],
    'essential oil': ['essential oil'],
    nut: ['almond', 'peanut', 'walnut', 'cashew', 'hazelnut', 'nut oil'],
    coconut: ['coconut', 'cocos nucifera']
  };

  const esc = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  function has(text, term) {
    const t = term.toLowerCase();
    return new RegExp(`\\b${esc(t)}${t.length <= 3 ? '\\b' : ''}`).test(text);
  }
  function stripHtml(html) {
    return (new DOMParser().parseFromString(html || '', 'text/html').body.textContent || '').replace(/\s+/g, ' ').trim();
  }
  function splitList(str) { return (str || '').toLowerCase().split(/[,;\n]| and /).map((s) => s.trim()).filter((s) => s.length > 1); }
  function skinAllergenTerms(str) {
    const out = new Set();
    splitList(str).forEach((tok) => {
      out.add(tok);
      Object.entries(SKIN_ALLERGEN_MAP).forEach(([k, v]) => { if (tok.includes(k)) v.forEach((x) => out.add(x)); });
    });
    return [...out];
  }

  async function loadCatalog() {
    if (state.catalog) return state.catalog;
    const all = [];
    for (let page = 1; page <= 4; page++) {
      const r = await fetch(`${cfg.shopRoot}collections/all/products.json?limit=250&page=${page}`, { headers: { Accept: 'application/json' } });
      if (!r.ok) throw new Error('catalogue unavailable');
      const j = await r.json();
      all.push(...(j.products || []));
      if ((j.products || []).length < 250) break;
    }
    state.catalog = all
      .filter((p) => (p.variants || []).some((v) => v.available))
      .map((p) => {
        const tags = Array.isArray(p.tags) ? p.tags : String(p.tags || '').split(',').map((t) => t.trim());
        const body = stripHtml(p.body_html);
        const head = `${p.title} ${p.product_type || ''} ${tags.join(' ')}`.toLowerCase();
        return { raw: p, id: p.id, title: p.title, handle: p.handle, body, benefitLines: extractBenefits(p.body_html), head, full: `${head} ${body.toLowerCase()}`, tags,
          image: p.images?.[0]?.src || '', variants: p.variants.filter((v) => v.available) };
      });
    return state.catalog;
  }

  /** Deterministic matching. Never invents products; skips anything whose suitability can't be established. */
  function recommend(analysis, profile, catalog) {
    const needs = [...(analysis.productNeeds || [])];
    if (!needs.length) {
      ['cleanser', 'moisturizer', 'mask'].forEach((c) => needs.push({ category: c, keywords: [], reason: 'A simple step for healthy-looking skin.' }));
    }
    const allergenTerms = skinAllergenTerms(profile.allergies);
    const concernTerms = (profile.concerns || []).flatMap((c) => CONCERN_TERMS[c] || []);
    const sensitive = profile.sensitivity !== 'no';
    const used = new Set();
    const primaries = [];
    const concernPicks = [];
    const alternatives = [];

    needs.forEach((need) => {
      const catTerms = CATEGORY_TERMS[need.category] || [];
      const scored = [];
      catalog.forEach((p) => {
        if (used.has(p.id)) return;
        const inHead = catTerms.some((t) => has(p.head, t));
        const inBody = !inHead && catTerms.some((t) => has(p.body.toLowerCase(), t));
        if (!inHead && !inBody) return;                          // wrong category
        if (p.body.length < 30 && !p.tags.length) return;         // too little information to judge suitability

        const textForAllergy = p.full.replace(/free (from|of)[^.;\n]*/g, '').replace(/\b\w+[- ]free\b/g, '');
        if (allergenTerms.some((t) => textForAllergy.includes(t))) return;  // stated allergy

        const kws = [...new Set([...(need.keywords || []).map((k) => k.toLowerCase()), ...concernTerms])];
        const hits = kws.filter((k) => has(p.full, k));
        const core = ['cleanser', 'moisturizer', 'mask', 'face_oil', 'exfoliant'].includes(need.category);
        if (!hits.length && !core) return;                        // cannot establish suitability

        const actives = STRONG_ACTIVES.filter((a) => has(p.full, a));
        if (actives.length && profile.sensitivity === 'yes') return;
        let score = (inHead ? 3 : 1) + Math.min(hits.length, 4);
        let caution = '';
        if (actives.length && sensitive) { score -= 2; caution = `Contains ${actives[0]}. Patch test first and introduce slowly.`; }
        scored.push({ product: p, need, score, hits, caution });
      });
      scored.sort((a, b) => b.score - a.score);
      if (scored[0]) { used.add(scored[0].product.id); primaries.push(scored[0]); }
      if (scored[1]) { used.add(scored[1].product.id); alternatives.push({ ...scored[1], alternative: true }); }
    });

    // Concern pass: traditional products often have names/types that don't fit a category, so also match each concern directly.
    const pairs = [];
    (profile.concerns || []).forEach((c) => {
      const terms = CONCERN_TERMS[c] || [];
      catalog.forEach((p) => {
        if (used.has(p.id)) return;
        if (allergenTerms.some((t) => p.full.replace(/free (from|of)[^.;\n]*/g, '').replace(/\b\w+[- ]free\b/g, '').includes(t))) return;
        const hits = terms.filter((t) => has(p.full, t));
        if (hits.length) pairs.push({ c, product: p, hits, terms, score: hits.length + (terms.some((t) => has(p.head, t)) ? 3 : 0) });
      });
    });
    pairs.sort((x, y) => y.score - x.score);                 // best (concern, product) pair first, so each concern gets its strongest match
    const doneConcerns = new Set();
    pairs.forEach((x) => {
      if (doneConcerns.has(x.c) || used.has(x.product.id)) return;
      doneConcerns.add(x.c); used.add(x.product.id);
      concernPicks.push({ product: x.product, hits: x.hits, score: x.score, need: { category: `concern:${x.c}`, keywords: x.terms, reason: `Picked for your ${label(x.c).toLowerCase()}.` }, caution: '' });
    });

    primaries.sort((a, b) => b.score - a.score);
    return [...concernPicks, ...primaries, ...alternatives].map((r) => ({
      ...r,
      reason: r.need.reason,
      benefits: pickBenefits(r.product, [...r.hits, ...concernTerms]),
      forConcerns: concernsFor(r.product, profile)
    }));
  }

  function shortDesc(text) {
    if (text.length <= 90) return text;
    return `${text.slice(0, 90).replace(/\s+\S*$/, '')}…`;
  }

  const catLabel = (c) => (String(c).startsWith('concern:') ? `For ${label(String(c).slice(8)).toLowerCase()}` : label(c));

  function productCard(r) {
    const p = r.product;
    const v0 = p.variants[0];
    const img = p.image ? `${p.image}${p.image.includes('?') ? '&' : '?'}width=600` : '';
    const select = p.variants.length > 1
      ? h('select', { 'aria-label': `Choose option for ${p.title}`, 'data-variant': p.id },
          p.variants.map((v) => h('option', { value: v.id, text: `${v.title} — ${money(v.price)}` })))
      : null;
    const btn = h('button', { type: 'button', class: 'jv-btn jv-btn--primary', 'data-add': p.id }, 'Add to cart');
    btn.addEventListener('click', () => addToCart(select ? select.value : v0.id, p.title, btn));
    return h('article', { class: 'jv-product' },
      h('div', { class: 'jv-product__img' }, img && h('img', { src: img, alt: p.raw.images?.[0]?.alt || p.title, loading: 'lazy', width: 600, height: 600 })),
      h('div', { class: 'jv-product__body' },
        h('span', { class: 'jv-product__tag', text: r.alternative ? `Alternative · ${catLabel(r.need.category)}` : catLabel(r.need.category) }),
        h('h3', { class: 'jv-product__title' }, h('a', { href: `${cfg.shopRoot}products/${p.handle}`, text: p.title })),
        h('div', { class: 'jv-product__price', text: money(v0.price) }),
        r.forConcerns?.length ? h('div', { class: 'jv-product__for' }, 'Good for', r.forConcerns.slice(0, 3).map((c) => h('b', { text: c }))) : null,
        r.benefits?.length
          ? h('ul', { class: 'jv-benefits', 'aria-label': 'Product benefits' }, r.benefits.map((b) => h('li', { text: b })))
          : h('p', { class: 'jv-muted jv-small', text: shortDesc(p.body) }),
        h('div', { class: 'jv-product__why' }, h('strong', { text: 'Why this: ' }), r.reason),
        r.caution && h('div', { class: 'jv-product__caution', text: r.caution }),
        h('div', { class: 'jv-product__foot' }, select, btn,
          h('a', { href: `${cfg.shopRoot}products/${p.handle}`, class: 'jv-small', text: 'View product details' }))
      ));
  }

  function paintProducts() {
    const grid = $('jv-products-grid'); clear(grid);
    const list = state.showAll ? state.recs.slice(0, 6) : state.recs.slice(0, 3);
    list.forEach((r) => grid.append(productCard(r)));
    show($('jv-more'), !state.showAll && state.recs.length > 3);
  }

  async function renderProducts() {
    const status = $('jv-products-status'), empty = $('jv-products-empty');
    state.showAll = false;
    try {
      const catalog = await loadCatalog();
      state.recs = recommend(state.analysis, state.profile, catalog);
    } catch {
      state.recs = [];
      status.textContent = '';
      showGeneral('We couldn’t load our product range just now, so here is some general guidance instead.');
      return;
    }
    if (!state.recs.length) {
      status.textContent = '';
      showGeneral('We couldn’t find a product in our range whose description clearly matches your needs and stated sensitivities, so here is some general guidance instead.');
      return;
    }
    show(empty, false);
    status.textContent = 'Always check the full ingredient list on the product page against your allergies.';
    paintProducts();
  }

  function showGeneral(intro) {
    const box = $('jv-products-empty'); clear(box);
    box.append(h('p', { text: intro }),
      h('ul', { class: 'jv-list' },
        h('li', { text: 'Wash gently once or twice a day.' }),
        h('li', { text: 'Moisturise daily.' }),
        h('li', { text: 'Try one new product at a time and patch test first.' })));
    show(box); clear($('jv-products-grid')); show($('jv-more'), false);
  }
  $('jv-more').addEventListener('click', () => { state.showAll = true; paintProducts(); });

  /* ------------------------------------------------------------------ cart (Dawn-compatible) */
  async function addToCart(variantId, title, btn) {
    const status = $('jv-cart-status');
    const original = btn.textContent;
    btn.disabled = true; btn.textContent = 'Adding…';
    try {
      const cartEl = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const body = { items: [{ id: Number(variantId), quantity: 1 }] };
      if (cartEl && typeof cartEl.getSectionsToRender === 'function') {
        body.sections = cartEl.getSectionsToRender().map((s) => s.id).join(',');
        body.sections_url = window.location.pathname;
      }
      const res = await fetch(`${cfg.shopRoot}cart/add.js`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body)
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.description || json.message || 'Could not add to cart');
      if (cartEl && typeof cartEl.renderContents === 'function' && json.sections) cartEl.renderContents(json);
      else await refreshCartBubble();
      if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
        try { window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'jevana-skin-analysis', cartData: json }); } catch { /* optional */ }
      }
      btn.textContent = 'Added ✓';
      status.textContent = `${title} added to your cart.`;
      setTimeout(() => { btn.textContent = original; btn.disabled = false; }, 1800);
    } catch (e) {
      btn.textContent = original; btn.disabled = false;
      status.textContent = `Sorry, we couldn't add ${title}: ${e.message}`;
    }
  }
  async function refreshCartBubble() {
    try {
      const r = await fetch(`${cfg.shopRoot}?sections=cart-icon-bubble`);
      const j = await r.json();
      const target = document.getElementById('cart-icon-bubble');
      if (target && j['cart-icon-bubble']) {
        const doc = new DOMParser().parseFromString(j['cart-icon-bubble'], 'text/html');
        const fresh = doc.getElementById('shopify-section-cart-icon-bubble');
        if (fresh) target.innerHTML = fresh.innerHTML;
      }
    } catch { /* cosmetic only */ }
  }

  /* ------------------------------------------------------------------ save / share / delete */
  function summaryText() {
    const a = state.analysis, p = state.profile;
    const L = [];
    L.push('JEVANA — MY SKINCARE ROUTINE', '');
    if (state.demo) L.push('(Demo: general guidance from my answers only; no photo analysis.)', '');
    L.push(`Skin type: ${label(p.skinType)}   Sensitivity: ${label(p.sensitivity)}`);
    if (p.concerns.length) L.push(`Concerns: ${p.concerns.map(label).join(', ')}`);
    L.push('', a.skinTypeGuidance, '');
    const steps = (title, arr) => {
      L.push(title);
      (arr || []).forEach((s, i) => {
        L.push(`${i + 1}. ${s.title} (${label(s.category)}) — ${s.howTo}`);
        if (s.frequency) L.push(`   How often: ${s.frequency}`);
        if (s.precautions) L.push(`   Note: ${s.precautions}`);
      });
      L.push('');
    };
    steps('MORNING', a.morningRoutine); steps('EVENING', a.eveningRoutine);
    if (state.recs.length) {
      L.push('PRODUCTS FROM JEVANA');
      state.recs.slice(0, 6).forEach((r) => {
        L.push(`- ${r.product.title} (${label(r.need.category)}): ${location.origin}${cfg.shopRoot}products/${r.product.handle}`);
        if (r.benefits?.length) L.push(`  Benefits: ${r.benefits.join('; ')}`);
      });
      L.push('');
    }
    if (a.ingredientsToAvoid?.length) { L.push('BE CAREFUL WITH'); a.ingredientsToAvoid.forEach((t) => L.push(`- ${t}`)); L.push(''); }
    L.push((cfg.answersOnly || state.skipped)
      ? 'This guidance provides general skincare information based on your answers. It is not a medical diagnosis and cannot replace advice from a qualified dermatologist.'
      : cfg.deviceScan
      ? 'This on-device photo scan gives rough, approximate observations about brightness and colour in your photo, combined with guidance from your answers. It is not a medical diagnosis and cannot replace advice from a qualified dermatologist.'
      : 'This AI-assisted assessment provides general skincare information based on visible characteristics and your answers. It is not a medical diagnosis and cannot replace advice from a qualified dermatologist.');
    return L.join('\n');
  }
  function saveMsg(t) { $('jv-save-status').textContent = t; }

  $('jv-copy').addEventListener('click', async () => {
    const text = summaryText();
    try { await navigator.clipboard.writeText(text); saveMsg('Routine copied to your clipboard.'); }
    catch {
      const ta = h('textarea', { 'aria-hidden': 'true', style: 'position:fixed;opacity:0' }); ta.value = text;
      document.body.append(ta); ta.select();
      try { document.execCommand('copy'); saveMsg('Routine copied.'); } catch { saveMsg('Copy is not supported in this browser — try Download instead.'); }
      ta.remove();
    }
  });
  $('jv-download').addEventListener('click', () => {
    const url = URL.createObjectURL(new Blob([summaryText()], { type: 'text/plain' }));
    const a = h('a', { href: url, download: 'jevana-skin-routine.txt' });
    document.body.append(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    saveMsg('Summary downloaded.');
  });

  function resetFlow(fullDelete) {
    stopLoader(); closeCamera(); clearPhoto();
    state.analysis = null; state.recs = []; state.profile = fullDelete ? null : state.profile;
    $('jv-consent').checked = false; updateAnalyzeBtn();
    wipe(); try { sessionStorage.removeItem(PENDING_KEY); } catch { /* noop */ } show($('jv-resume'), false);
    if (fullDelete) $('jv-form-profile').reset();
    show($('jv-results-wrap'), false);
    showPanel(1);
  }
  $('jv-restart').addEventListener('click', () => resetFlow(false));
  $('jv-delete').addEventListener('click', () => {
    resetFlow(true);
    $('jv-profile-error').textContent = 'Your photo, answers and results have been deleted from this device.';
    show($('jv-profile-error'));
    $('jv-profile-error').style.color = 'inherit';
  });

  $('jv-resume').addEventListener('click', async () => {
    const saved = loadSaved();
    if (!saved?.analysis) return;
    state.profile = saved.profile; state.analysis = saved.analysis; state.demo = saved.demo; state.skipped = !!saved.skipped;
    await renderAll(true);
  });

  /* ------------------------------------------------------------------ demo mode (answers only; clearly labelled, never claims to read a photo) */
  const ALLERGEN_MAP = {
    nut: ['almond', 'walnut', 'cashew', 'pistachio', 'peanut', 'groundnut', 'hazelnut', 'nuts'],
    peanut: ['peanut', 'groundnut'],
    dairy: ['milk', 'curd', 'yogurt', 'yoghurt', 'buttermilk', 'paneer', 'ghee', 'cheese', 'dairy', 'lassi'],
    milk: ['milk', 'curd', 'yogurt', 'yoghurt', 'buttermilk', 'paneer', 'ghee', 'cheese', 'dairy', 'lassi'],
    egg: ['egg'], fish: ['fish', 'seafood', 'salmon', 'sardine', 'mackerel'], shellfish: ['prawn', 'shrimp', 'crab'],
    soy: ['soy', 'tofu', 'tempeh'], sesame: ['sesame'], coconut: ['coconut'],
    gluten: ['wheat', 'gluten', 'roti', 'chapati', 'semolina', 'rava'], wheat: ['wheat', 'gluten', 'roti', 'chapati', 'semolina', 'rava'],
    lentil: ['dal', 'lentil', 'legume', 'chickpea', 'sprouts', 'sambar'], legume: ['dal', 'lentil', 'legume', 'chickpea', 'sprouts', 'sambar']
  };
  function foodAllergenTerms(str) {
    const out = new Set();
    splitList(str).forEach((tok) => { out.add(tok); Object.entries(ALLERGEN_MAP).forEach(([k, v]) => { if (tok.includes(k)) v.forEach((x) => out.add(x)); }); });
    return [...out];
  }
  const ALL = ['vegetarian', 'vegan', 'non-vegetarian', 'no-preference'];
  const DAIRY_OK = ['vegetarian', 'non-vegetarian', 'no-preference'];
  const NONVEG = ['non-vegetarian', 'no-preference'];
  const FOODS = {
    hydration: [
      ['Sip water steadily through the day and keep a bottle within reach.', ALL],
      ['Tender coconut water or a light herbal infusion (cumin–coriander–fennel) can be refreshing options.', ALL],
      ['Buttermilk (neer mor) with curry leaves and ginger is a traditional cooling drink.', DAIRY_OK]
    ],
    meals: [
      ['A simple plate: millet or rice, dal or sambar, a seasonal vegetable poriyal and a portion of greens (keerai).', ALL],
      ['Ragi porridge or vegetable upma for a fibre-rich breakfast.', ALL],
      ['Idli or dosa with sambar and a chutney, plus a side of fruit.', ALL]
    ],
    protein: [
      ['Dal, sprouted moong, chickpeas or rajma in everyday meals.', ALL],
      ['Curd or paneer as part of a balanced meal.', DAIRY_OK],
      ['Tofu or soya chunks in a vegetable curry.', ALL],
      ['Eggs, fish or chicken, grilled or curried with minimal oil.', NONVEG]
    ],
    fruitsVegetables: [
      ['Papaya, guava, citrus fruits, pomegranate or seasonal berries.', ALL],
      ['Leafy greens such as keerai or drumstick leaves, plus carrots, tomatoes and bell peppers.', ALL]
    ],
    healthyFats: [
      ['A small handful of almonds or walnuts, or a spoon of flax or pumpkin seeds.', ALL],
      ['Cooking with modest amounts of cold-pressed groundnut, sesame or coconut oil.', ALL],
      ['Oily fish such as sardines or mackerel a couple of times a week.', NONVEG]
    ],
    sleepStress: [
      ['Aim for a regular bedtime and enough sleep for how you feel the next day.', ALL],
      ['Take 10 minutes daily for a walk, stretching or slow breathing.', ALL],
      ['Keep screens away for a while before bed.', ALL]
    ],
    checklist: [
      ['Drank water through the day', ALL], ['Greens or vegetables at two meals', ALL], ['Moisturised after washing', ALL],
      ['Cleansed before bed', ALL], ['A short walk or stretch', ALL]
    ]
  };
  function demoFood(profile) {
    const bad = foodAllergenTerms(profile.foodAllergies);
    const out = {};
    Object.entries(FOODS).forEach(([k, items]) => {
      out[k] = items.filter(([t, diets]) => diets.includes(profile.diet) && !bad.some((b) => t.toLowerCase().includes(b))).map(([t]) => t);
    });
    return out;
  }

  const TYPE_GUIDE = {
    oily: 'Oily skin likes light, non-greasy care.',
    dry: 'Dry skin needs soft, nourishing care.',
    combination: 'Combination skin needs light care, with extra moisture only where dry.',
    normal: 'Normal skin needs a simple, steady routine.',
    unsure: 'Start simple and see how your skin feels through the day.'
  };
  const CONCERN_NOTE = 'You listed this as a concern.';
  const EXPLORE = {
    acne: 'Neem, tulsi, sandalwood',
    pores: 'Clay (multani mitti), sandalwood, rose',
    uneven_tone: 'Turmeric, saffron, kumkumadi-type care',
    dryness: 'Aloe, almond, coconut, honey, ghee',
    excess_oil: 'Light gels, clay (multani mitti), neem',
    dullness: 'Turmeric, saffron, honey, rose',
    fine_lines: 'Nourishing oils like almond, saffron'
  };
  const KW = { oily: ['gel', 'oil control', 'balanc'], dry: ['hydrat', 'cream', 'ceramide'], combination: ['balanc', 'hydrat'], normal: ['hydrat', 'gentle'], unsure: ['gentle', 'hydrat'] };

  function demoAnalysis(p) {
    const sensitive = p.sensitivity !== 'no';
    const patch = sensitive ? 'Patch test first.' : undefined;
    const kw = KW[p.skinType] || [];
    const needs = [
      { category: 'cleanser', keywords: ['gentle', ...kw], reason: 'Gentle daily cleansing for your skin.' },
      { category: 'moisturizer', keywords: kw.length ? kw : ['hydrat'], reason: 'Keeps skin soft and comfortable.' },
      { category: 'mask', keywords: [], reason: 'A weekly boost for your skin.' }
    ];
    const avoid = splitList(p.allergies).map((a) => `${a} (you listed this)`);
    avoid.push(sensitive ? 'Fragrance and harsh scrubs.' : 'Over-scrubbing.');
    return {
      demo: true, photoUsable: true, photoNote: '',
      observations: [],
      possibleConcerns: p.concerns.map((c) => ({ concern: label(c), note: CONCERN_NOTE })),
      attentionAreas: [],
      skinTypeGuidance: TYPE_GUIDE[p.skinType],
      morningRoutine: [
        { category: 'cleanser', title: 'Cleanse', howTo: 'Wash with lukewarm water and a gentle cleanser.', frequency: 'Every morning', precautions: patch },
        { category: 'moisturizer', title: 'Moisturise', howTo: 'Apply a thin layer on face and neck.', frequency: 'Every morning' }
      ],
      eveningRoutine: [
        { category: 'cleanser', title: 'Cleanse', howTo: 'Wash off the day’s dust and oil.', frequency: 'Every evening' },
        { category: 'moisturizer', title: 'Moisturise', howTo: 'Lock in moisture.', frequency: 'Every evening' },
        { category: 'mask', title: 'Face pack (optional)', howTo: 'Apply, then rinse off as directed.', frequency: 'Once a week', precautions: patch }
      ],
      ingredientsToExplore: p.concerns.map((c) => EXPLORE[c]).filter(Boolean),
      ingredientsToAvoid: avoid,
      dailyHabits: ['Keep the routine simple and steady.', 'Don’t pick or scrub your skin.', 'Drink enough water.', 'Change pillow covers often.'],
      productNeeds: needs,
      lifestyle: demoFood(p),
      seeDermatologist: { suggested: false, reason: '' }
    };
  }


  /* ------------------------------------------------------------------ on-device scan (no API; the photo never leaves the browser)
   * Uses MediaPipe Face Landmarker (face detection only) + simple brightness/colour statistics.
   * It is deliberately modest: it compares regions of the SAME face against each other (which reduces
   * skin-tone bias), flags only clear differences, and never claims to detect acne, pores or lines. */
  const MP_BASE = 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.14';
  const MP_MODEL = 'https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task';
  let landmarkerPromise = null;

  function getLandmarker() {
    if (!landmarkerPromise) {
      landmarkerPromise = (async () => {
        const mod = await import(`${MP_BASE}/vision_bundle.mjs`);
        const fileset = await mod.FilesetResolver.forVisionTasks(`${MP_BASE}/wasm`);
        const make = (delegate) => mod.FaceLandmarker.createFromOptions(fileset, {
          baseOptions: { modelAssetPath: MP_MODEL, delegate }, runningMode: 'IMAGE', numFaces: 2
        });
        try { return await make('GPU'); } catch { return await make('CPU'); }
      })().catch((e) => { landmarkerPromise = null; throw e; });
    }
    return landmarkerPromise;
  }

  function loadImage(url) {
    return new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = url; });
  }

  function sampleEllipse(data, W, H, e) {
    const x0 = Math.max(0, Math.floor(e.cx - e.rx)), x1 = Math.min(W - 1, Math.ceil(e.cx + e.rx));
    const y0 = Math.max(0, Math.floor(e.cy - e.ry)), y1 = Math.min(H - 1, Math.ceil(e.cy + e.ry));
    const Ls = []; let rg = 0;
    for (let y = y0; y <= y1; y++) {
      for (let x = x0; x <= x1; x++) {
        const dx = (x - e.cx) / e.rx, dy = (y - e.cy) / e.ry;
        if (dx * dx + dy * dy > 1) continue;
        const i = (y * W + x) * 4;
        const R = data[i], G = data[i + 1], B = data[i + 2];
        rg += R / (G + 1);
        Ls.push(0.2126 * R + 0.7152 * G + 0.0722 * B);
      }
    }
    const n = Ls.length;
    if (n < 30) return null;
    return { n, Ls, rg: rg / n, L: Ls.reduce((a, v) => a + v, 0) / n };
  }
  function pool(a, b) {
    if (!a || !b) return a || b;
    const n = a.n + b.n;
    return { n, Ls: a.Ls.concat(b.Ls), rg: (a.rg * a.n + b.rg * b.n) / n, L: (a.L * a.n + b.L * b.n) / n };
  }
  function laplacianVar(data, W, H, box) {
    const step = Math.max(1, Math.round(box.w / 160));
    const gw = Math.floor(box.w / step), gh = Math.floor(box.h / step);
    if (gw < 8 || gh < 8) return 999;
    const g = new Float32Array(gw * gh);
    for (let y = 0; y < gh; y++) {
      for (let x = 0; x < gw; x++) {
        const px = Math.min(W - 1, Math.floor(box.x + x * step)), py = Math.min(H - 1, Math.floor(box.y + y * step));
        const i = (py * W + px) * 4;
        g[y * gw + x] = 0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2];
      }
    }
    let sum = 0, sum2 = 0, n = 0;
    for (let y = 1; y < gh - 1; y++) {
      for (let x = 1; x < gw - 1; x++) {
        const v = -4 * g[y * gw + x] + g[(y - 1) * gw + x] + g[(y + 1) * gw + x] + g[y * gw + x - 1] + g[y * gw + x + 1];
        sum += v; sum2 += v * v; n++;
      }
    }
    const mean = sum / n;
    return sum2 / n - mean * mean;
  }

  async function deviceAnalysis() {
    if (!state.previewUrl) throw new Error('Please add a photo first.');
    let landmarker;
    try { landmarker = await getLandmarker(); }
    catch { throw new Error('The on-device scanner could not load. It needs an internet connection the first time. Please try again, or continue with your answers only.'); }

    const img = await loadImage(state.previewUrl);
    const scale = Math.min(1, 720 / Math.max(img.naturalWidth, img.naturalHeight));
    const W = Math.round(img.naturalWidth * scale), H = Math.round(img.naturalHeight * scale);
    const canvas = document.createElement('canvas'); canvas.width = W; canvas.height = H;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(img, 0, 0, W, H);

    const faces = landmarker.detect(canvas).faceLandmarks || [];
    if (!faces.length) throw new Error('We could not find a face in that photo. Please retake it facing the camera in good light.');
    if (faces.length > 1) throw new Error('Please use a photo with only your face in it.');
    const lm = faces[0];
    const P = (i) => ({ x: lm[i].x * W, y: lm[i].y * H });
    const top = P(10), chin = P(152), fl = P(234), fr = P(454), nose = P(1), bridge = P(168), glab = P(9), lip = P(17);
    const faceW = Math.abs(fr.x - fl.x), faceH = chin.y - top.y;
    if (faceW / W < 0.25) throw new Error('Your face looks too small in the photo. Please move closer and retake it.');
    if (Math.abs((nose.x - (fl.x + fr.x) / 2) / faceW) > 0.18) throw new Error('Please face the camera straight on and retake the photo.');

    const c1 = P(50), c2 = P(280), e1 = P(145), e2 = P(374);
    const lc = c1.x < c2.x ? c1 : c2, rc = c1.x < c2.x ? c2 : c1;
    const le = e1.x < e2.x ? e1 : e2, re = e1.x < e2.x ? e2 : e1;
    const cheek = (c) => ({ cx: c.x, cy: c.y, rx: 0.10 * faceW, ry: 0.08 * faceH });
    const under = (e) => ({ cx: e.x, cy: e.y + 0.035 * faceH, rx: 0.07 * faceW, ry: 0.018 * faceH });
    const data = ctx.getImageData(0, 0, W, H).data;
    const EL = {
      forehead: { cx: bridge.x, cy: top.y + 0.55 * (glab.y - top.y), rx: 0.26 * faceW, ry: 0.3 * (glab.y - top.y) },
      nose: { cx: nose.x, cy: (bridge.y + nose.y) / 2, rx: 0.05 * faceW, ry: 0.4 * (nose.y - bridge.y) },
      left_cheek: cheek(lc), right_cheek: cheek(rc),
      chin: { cx: chin.x, cy: (lip.y + chin.y) / 2, rx: 0.10 * faceW, ry: 0.4 * (chin.y - lip.y) },
      left_eye: under(le), right_eye: under(re)
    };
    const S = {
      forehead: sampleEllipse(data, W, H, EL.forehead),
      nose: sampleEllipse(data, W, H, EL.nose),
      left_cheek: sampleEllipse(data, W, H, EL.left_cheek),
      right_cheek: sampleEllipse(data, W, H, EL.right_cheek),
      chin: sampleEllipse(data, W, H, EL.chin),
      under_eyes: pool(sampleEllipse(data, W, H, EL.left_eye), sampleEllipse(data, W, H, EL.right_eye))
    };
    const norm = (e) => ({ cx: e.cx / W, cy: e.cy / H, rx: e.rx / W, ry: e.ry / H });
    const marks = [];
    if (!S.forehead || !S.nose || !S.left_cheek || !S.right_cheek || !S.chin || !S.under_eyes) {
      throw new Error('We could not read your face clearly. Please retake the photo in soft daylight, facing the camera.');
    }

    const faceL = (S.forehead.L + S.nose.L + S.left_cheek.L + S.right_cheek.L + S.chin.L) / 5;
    if (faceL < 60) throw new Error('The photo is too dark. Please retake it facing a window or soft daylight.');
    if (faceL > 215) throw new Error('The photo is too bright or washed out. Please retake it away from direct flash or harsh light.');

    const thr = faceL * 1.22;
    const hl = (r) => r.Ls.filter((v) => v > thr).length / r.n;
    const refRG = (S.forehead.rg + S.chin.rg) / 2;
    const cheekL = (S.left_cheek.L + S.right_cheek.L) / 2;
    const tzone = (hl(S.forehead) + hl(S.nose)) / 2;
    const cheekHL = (hl(S.left_cheek) + hl(S.right_cheek)) / 2;

    const obs = [], areas = [], flags = {};
    const redSides = [['left_cheek', S.left_cheek], ['right_cheek', S.right_cheek]].filter(([, r]) => r.rg / refRG > 1.14);
    if (redSides.length) {
      flags.redness = true;
      obs.push({ area: 'Cheeks', note: 'Cheeks look slightly redder than the rest of your face (lighting can cause this).' });
      redSides.forEach(([region]) => areas.push({ region, note: 'Appears slightly redder than the forehead and chin.' }));
      marks.push({ label: 'Slight redness', where: 'Cheeks', shapes: redSides.map(([region]) => norm(EL[region])) });
    }
    if (tzone > 0.10 && tzone > 2 * cheekHL) {
      flags.shine = true;
      obs.push({ area: 'Forehead and nose', note: 'More shine here than on your cheeks (could be oil or lighting).' });
      areas.push({ region: 'forehead', note: 'More bright reflections than the cheeks.' }, { region: 'nose', note: 'More bright reflections than the cheeks.' });
      marks.push({ label: 'Extra shine (oil)', where: 'Forehead and nose', shapes: [norm(EL.forehead), norm(EL.nose)] });
    }
    if (S.under_eyes.L / cheekL < 0.86) {
      flags.underEye = true;
      obs.push({ area: 'Under-eyes', note: 'Under-eyes look a little darker than your cheeks (tiredness or lighting).' });
      areas.push({ region: 'under_eyes', note: 'Looks a little darker than the cheeks.' });
      marks.push({ label: 'Darker under-eyes', where: 'Under the eyes', shapes: [norm(EL.left_eye), norm(EL.right_eye)] });
    }
    if (!obs.length) obs.push({ area: 'Overall', note: 'Nothing stood out in this photo.' });
    const box = { x: Math.max(0, Math.min(fl.x, fr.x)), y: Math.max(0, top.y), w: Math.min(W, faceW), h: Math.min(H - top.y, faceH) };
    if (laplacianVar(data, W, H, { x: box.x, y: box.y, w: Math.max(8, box.w), h: Math.max(8, box.h) }) < 6) {
      obs.push({ area: 'Photo quality', note: 'Photo looks a bit blurry, so this is less reliable. Try daylight.' });
    }
    obs.push({ area: 'About this scan', note: 'This scan only compares brightness and colour. Pores, lines and blemishes come from your answers.' });

    const a = demoAnalysis(state.profile);
    a.demo = false; a.photoUsable = true; a.photoNote = '';
    a.observations = obs; a.attentionAreas = areas; a.photoMarks = marks;
    if (flags.shine) a.skinTypeGuidance += ' Your photo showed extra shine around the forehead and nose; if that matches how your skin feels, lighter, non-greasy textures may suit you.';
    const need = (cat) => a.productNeeds.find((n) => n.category === cat);
    if (flags.redness) {
      a.ingredientsToExplore.push('Soothing, fragrance-free formulas (for example aloe, centella or panthenol). Your photo showed some extra redness, which has many possible causes.');
      if (need('moisturizer')) need('moisturizer').keywords.push('soothing', 'calming');
    }
    if (flags.shine && state.profile.skinType !== 'dry' && need('moisturizer')) need('moisturizer').keywords.push('gel', 'oil control');
    return a;
  }

  $('jv-skip').addEventListener('click', async () => {
    state.skipped = true;
    state.analysis = demoAnalysis(state.profile);
    persist();
    await renderAll(true);
  });

  /* ------------------------------------------------------------------ init */
  function init() {
    show($('jv-demo-banner'), cfg.photo && !cfg.deviceScan && !cfg.backend);
    if (cfg.answersOnly) show($('jv-error-back'), false);
    if (cfg.photo && !cfg.deviceScan && !cfg.backend) {
      $('jv-analyze').textContent = 'Get guidance (demo)';
      const span = document.querySelector('#jv-consent + span');
      if (span) span.textContent = 'I understand this is a demo: nothing is sent anywhere, and the guidance is general information, not a medical diagnosis.';
    }
    updateAnalyzeBtn();
    if (cfg.deviceScan) $('jv-analyze').textContent = cfg.loggedIn ? 'Scan my photo' : 'Log in & scan my photo';
    else if (!cfg.loggedIn && cfg.photo) $('jv-analyze').textContent = cfg.backend ? 'Log in & analyse my skin' : 'Log in & get guidance (demo)';
    if (loadSaved()?.analysis) show($('jv-resume'));
    refreshLoginLabels();
    setTimeout(refreshLoginLabels, 1500); setTimeout(refreshLoginLabels, 4000);   // KwikPass loads a little later
    let hasPending = false;
    try { hasPending = !!sessionStorage.getItem(PENDING_KEY); } catch { /* ignore */ }
    if (cfg.loggedIn) resumePending();
    else if (hasPending) waitFor(kwikLoggedIn, 8000).then((ok) => { if (ok) resumePending(); });
  }
  init();
})();